import { FieldType, ViewModelBase } from "@shoothill/core";
import { action, computed, observable, runInAction } from "mobx";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import moment from "moment";
import { AppUrls } from "AppUrls";
import { StoresInstance } from "Globals/Stores";
import validator from "validator";

// View models
import FormSectionViewModelBase from "Globals/ViewModels/FormSections/FormSectionViewModelBase";

// Models
import { NewPropertyDetailsModel } from "Globals/Models/Domain/FormSections/NewPropertyDetails/NewPropertyDetailsModel";
import { NewPropertyDetailDTO } from "Globals/Models/DTO/NewPropertyDetailDTO";
import { FormSectionsEnum } from "Globals/Models/FormSectionsEnum";
import { HomeOwnerStatusEnum } from "Globals/Models/HomeOwnerStatusEnum";

export default class NewPropertyDetailsViewModel extends FormSectionViewModelBase<NewPropertyDetailsModel> {
    constructor(newPropertyDetailsModel: NewPropertyDetailsModel, _updateSectionCompleted: (index: FormSectionsEnum, value: boolean) => void) {
        super(_updateSectionCompleted, newPropertyDetailsModel);
        this.saveStore.addCallback(FormSectionsEnum.NewPropertyDetails, this.save);
        this.saveFunction = this.save;
        if (this.updateSectionCompleted) {
            this.updateSectionCompleted(FormSectionsEnum.NewPropertyDetails, this.canProceed);
        }
    }

    public save = async (): Promise<void> => {
        try {
            //debugger;
            let isMyModelValid: boolean = await this.isMyModelValid();
            let isMyModelDirty: boolean = this.isModelDirty;
            if (isMyModelValid && isMyModelDirty === true) {
                this.setIsLoading(true);
                this.saveStore.setIsSaving(true);

                let upsertNewPropertyDetails: NewPropertyDetailDTO = this.model.toDto();
                const apiResult = await this.Post<NewPropertyDetailDTO>(AppUrls.Server.ApplicationForm.Upsert.NewPropertyDetails, upsertNewPropertyDetails);

                if (apiResult.wasSuccessful) {
                    this.model.fromDto(apiResult.payload);
                    console.info("NewPropertyDetails:Upsert-Successful");
                    // Will set this section as completed when canProceed returns true
                    this.updateSectionCompleted(FormSectionsEnum.NewPropertyDetails, this.canProceed);

                    this.setModelDirty(false);
                    return Promise.resolve();
                } else {
                    console.error("NewPropertyDetails:Upsert-Error", apiResult.errors);
                    return Promise.reject(apiResult.errors);
                }
            }
        } catch (exception) {
            console.log("Error:", exception);
        } finally {
            this.setIsLoading(false);
            this.saveStore.setIsSaving(false);
            this.saveStore.setIsSaved(true);
        }
    };
    /**
     * Custom model validation function. Validates child view models.
     * @returns boolean If model is valid or not.
     */
    private isMyModelValid = async (): Promise<boolean> => {
        let isValid = true;
        if ((await this.isModelValid()) == false) {
            isValid = false;
        }
        return isValid;
    };

    @action
    public handleInputChange = (): void => {
        this.saveStore.setIsSaved(false);
        this.restartTimer();
        this.saveFunction = this.save;
        this.updateSectionCompleted(FormSectionsEnum.NewPropertyDetails, this.canProceed);
    };

    @action
    public handleCheckboxChange = (fieldName: keyof FieldType<NewPropertyDetailsModel>, val: boolean): void => {
        this.model.setValue(fieldName, val);
        this.model.setDirty(fieldName, true);
        this.handleInputChange();
    };
    @action
    public handleFieldChange = (fieldName: keyof FieldType<NewPropertyDetailsModel>, val: string): void => {
        this.model.setValue(fieldName, val);
        this.model.setDirty(fieldName, true);
        this.handleInputChange();
    };
    @action
    public handleRadioChange = (fieldName: keyof FieldType<NewPropertyDetailsModel>, val: string): void => {
        this.model.setValue(fieldName, val == "1" ? true : false);
        this.model.setDirty(fieldName, true);
        this.handleInputChange();
    };

    @action
    public setDate(fieldName: keyof FieldType<NewPropertyDetailsModel>, val: MaterialUiPickersDate) {
        if (val !== null) {
            this.model.setValue(fieldName, moment(val).utc().toISOString());
        } else {
            this.model.setValue(fieldName, val);
        }
        this.handleInputChange();
    }

    @computed
    public get canProceed(): boolean {
        let completed = true;
        Object.keys(this.model).forEach((_field) => {
            let field = _field as keyof FieldType<NewPropertyDetailsModel>;
            let value = this.model.getValue(field);
            if (field == "createdDate") return;
            if (field == "isDeleted") return;
            if (field == "propertyTypeId") return;
            if (field == "addressLine2") return;
            if (field == "addressLine3") return;
            if (field == "secondChoiceAddressLine1") return;
            if (field == "secondChoiceAddressLine2") return;
            if (field == "secondChoiceAddressLine3") return;
            if (field == "thirdChoiceAddressLine1") return;
            if (field == "thirdChoiceAddressLine2") return;
            if (field == "thirdChoiceAddressLine3") return;
            if (field == "city") return;
            if (field == "county") return;
            if (field == "postcode") return;
            if (this.isEmptyNullOrUndefined(value)) {
                completed = false;
            }
            if (completed == false) {
                console.error("Field not completed: ", field, "Value: ", value);
            }
        });
        return completed;
    }

    public async isFieldValid(fieldName: keyof FieldType<NewPropertyDetailsModel>, value: any): Promise<boolean> {
        return await this.isValid(fieldName, value);
    }

    public async isValid(fieldName: keyof FieldType<NewPropertyDetailsModel>, value: any, isRequired: boolean = false): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);

        if (fieldName == "developmentName") {
            if ((value && !validator.isEmpty(value)) || isRequired) {
                if (validator.isEmpty(value)) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }
        if (fieldName == "addressLine1") {
            if ((value && !validator.isEmpty(value)) || isRequired) {
                if (validator.isEmpty(value)) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }
        {
            /* SW removed for 11548 */
        }
        // if (fieldName == "city") {
        //     if ((value && !validator.isEmpty(value)) || isRequired) {
        //         if (validator.isEmpty(value)) {
        //             errorMessage = "required to proceed";
        //             isValid = false;
        //         }
        //     }
        // }
        // if (fieldName == "county") {
        //     if ((value && !validator.isEmpty(value)) || isRequired) {
        //         if (validator.isEmpty(value)) {
        //             errorMessage = "required to proceed";
        //             isValid = false;
        //         }
        //     }
        // }
        // if (fieldName == "postcode") {
        //     if ((value && !validator.isEmpty(value)) || isRequired) {
        //         if (validator.isEmpty(value)) {
        //             errorMessage = "required to proceed";
        //             isValid = false;
        //         }
        //         if (!validator.isPostalCode(value, "GB")) {
        //             errorMessage = "not a valid postcode";
        //             isValid = false;
        //         }
        //     }
        // }
        if (isValid == false) {
            console.log("Field Not valid: ", fieldName);
        }
        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);
        return isValid;
    }

    /**
     * @returns boolean does this section have any errors
     */
    @action
    public async showUnfinishedFields(): Promise<boolean> {
        let hasError = false;
        for (const _field of Object.keys(this.model)) {
            let field = _field as keyof FieldType<NewPropertyDetailsModel>;
            let value = this.model.getValue(field);
            let isRequired = true;
            let isValid = await this.isValid(field, value, isRequired);
            if (isValid == false) {
                hasError = true;
            }
        }
        return hasError;
    }
    public beforeUpdate: undefined;
}
